import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { Loading } from '../index';
import { useAuth } from '../../auth';
import { NoPermission } from '../NoPermission';

/**
 * MemberProtectedRoute component that restricts access based on user type
 * Uses only session data populated during signin - no API calls
 * 
 * @param {Object} props - Component props
 * @param {React.Component} props.component - Component to render if authorized
 * @param {number|number[]} props.requiredTypes - Required user type(s) to access this route
 * @param {string|string[]} props.path - Path(s) to protect
 * @param {Object} props.rest - Additional props to pass to Route
 */
export function MemberProtectedRoute({ 
  component: Component, 
  requiredTypes, 
  path,
  ...rest 
}) {
  const auth = useAuth();
  const session = auth.sessionV2 || auth.session || auth.user;
  
  // Convert requiredTypes to array if it's a single number
  const requiredTypesArray = Array.isArray(requiredTypes) ? requiredTypes : [requiredTypes];

  // Convert path to array if it's a single string
  const pathArray = Array.isArray(path) ? path : [path];
  
  // Helper function to check if user has access based on member type level
  const hasAccessByLevel = (userMemberType, requiredType) => {
    // Special case for Admin (33) - has access to everything
    if (userMemberType === 33) {
      return true;
    }
    
    // For regular member types, check if user's member type is >= required type
    // This means higher member types have access to more paths
    return userMemberType >= requiredType;
  };
  
  const hasCheckedAuth = auth.authChecked;

  // Get user data directly from session (populated during signin via signinV2)
  // This data comes from fetchUserProfileAndRoles() called during authentication
  // Fix: Use correct memberType - prefer session over sessionV2 if sessionV2 has wrong data
  const userMemberType = auth.session?.memberType || auth.sessionV2?.memberType;

  
  // If multiple paths, create multiple routes
  if (pathArray.length > 1) {
    return (
      <>
        {pathArray.map((singlePath, index) => (
          <Route
            key={`${singlePath}-${index}`}
            path={singlePath}
            exact
            {...rest}
            render={props => {
              // Show loading briefly while checking auth
              if (!hasCheckedAuth) {
                return (
                  <div style={{padding: '2rem', textAlign: 'center'}}>
                    <Loading />
                    <p>Checking authentication...</p>
                  </div>
                );
              }
              
              // Check if user is logged in
              if (!session) {
                return <Redirect to={`/login?returnTo=${encodeURIComponent(props.location.pathname)}`} />;
              }
              
                             // Check if the user has the required member type level
               const hasAccess = userMemberType && 
                 requiredTypesArray.some(requiredType => 
                   hasAccessByLevel(userMemberType, requiredType)
                 );
               
              if (hasAccess) {
                return <Component {...props} />;
              } else {
                return <NoPermission 
                  title="Insufficient Membership Level"
                  message={`You need membership level ${requiredTypesArray.join(' or ')} or higher to access this page. Your current level is ${userMemberType || 'not set'}.`}
                />;
              }
            }}
          />
        ))}
      </>
    );
  }
  
  // Single path route
  return (
    <Route
      path={path}
      exact
      {...rest}
      render={props => {
        // Show loading briefly while checking auth
        if (!hasCheckedAuth) {
          return (
            <div style={{padding: '2rem', textAlign: 'center'}}>
              <Loading />
              <p>Checking authentication...</p>
            </div>
          );
        }
        
        // Check if user is logged in
        if (!session) {
          return <Redirect to={`/login?returnTo=${encodeURIComponent(props.location.pathname)}`} />;
        }
        
                 // Check if the user has the required member type level
         const hasAccess = userMemberType && 
           requiredTypesArray.some(requiredType => 
             hasAccessByLevel(userMemberType, requiredType)
           );
        
         // Check if the user has the required access
         if (hasAccess) {
           return <Component {...props} />;
         } else {
            return <NoPermission 
              title="Insufficient Membership Level"
              message={`You need membership level ${requiredTypesArray.join(' or ')} or higher to access this page. Your current level is ${userMemberType || 'not set'}.`}
            />;
         }
       }}
     />
   );
} 